import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { Button, Card, Col, DatePicker, Form, Row, Select, Table, Typography } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import ReactHTMLTableToExcel from 'react-html-table-to-excel';
import { useForm } from 'antd/lib/form/Form';
import { UnitcodeService } from 'libs/shared-services/masters/src/lib/unit-code.services';
import { PlantsDropDown } from '@gtpl/shared-models/masters';
import { AlertMessages } from '@gtpl/shared-utils/alert-messages';
import { SaleOrderService } from '@gtpl/shared-services/sale-management';
import { DutyDrawBackReportReq } from '@gtpl/shared-models/finance';

const VendorAgingWiseReport: React.FC = () => {
  const [unitId, setUnitId] = useState<number>(0);
  const [vendorName,setVendorName] = useState<string> (null)
  const [vendorsData,setVendorsData] = useState<any[]>([])
  const [reportData, setReportData] = useState<any | null>(null);
  const [plantData, setPlantData] = useState<PlantsDropDown[]>([]);
  const [page, setPage] = useState(1);
  const [fromDate, setFromDate] = useState<string | undefined>(undefined);
  const [toDate, setToDate] = useState<string | undefined>(undefined);
  const { RangePicker } = DatePicker;
  const unitsService = new UnitcodeService();
  const saleOrderService = new SaleOrderService();
  const { Text, Title } = Typography;
  const { Option } = Select;
  const [form] = useForm();

  useEffect(() => {
    getAllPlants();
    getVendorData();
    getAllVendors()
  }, []);

  const getAllPlants = () => {
    unitsService
      .getAllMainPlants()
      .then((res) => {
        if (res.status) {
          setPlantData(res.data);
        } else {
          setPlantData([]);
        }
      })
      .catch((err) => {
        AlertMessages.getErrorMessage(err.message);
        setPlantData([]);
      });
  };

      const getAllVendors = () => {
      saleOrderService
        .getVendorsData()
        .then((res) => {
          if (res.status) {
            setVendorsData(res.data);
          } else {
            setVendorsData([]);
          }
        })
        .catch((err) => {
          AlertMessages.getErrorMessage(err.message);
          setVendorsData([]);
        });
    };
  

  const handleUnit = (value: number) => {
    setUnitId(value);
    form.setFieldsValue({ unitId: value }); 
  };

    const handleVendor = (value: string) => {
    setVendorName(value);
    form.setFieldsValue({ supplier: value }); 
  };

  const handleDateRange = (dates: any) => {
    if (dates && dates.length === 2) {
      setFromDate(dates[0].format('YYYY-MM-DD'));
      setToDate(dates[1].format('YYYY-MM-DD'));
    } else {
      setFromDate(undefined);
      setToDate(undefined);
    }
  };

  const getVendorData = () => {
    const req = new DutyDrawBackReportReq();
    if (fromDate) {
      req.fromDate = fromDate;
    }
    if (toDate) {
      req.toDate = toDate;
    }
    if (unitId) {
      req.unitId = unitId;
    }
    if (vendorName) {
      req.vendorName = vendorName;
    }
    saleOrderService
      .getVendorAgingWiseReport(req)
      .then((res) => {
        if (res.status) {
          setReportData(res.data);
        } else {
          setReportData(null);
        }
      })
      .catch((err) => {
        AlertMessages.getErrorMessage(err.message);
        setReportData(null);
      });
  };


  const onReset = () => {
    form.resetFields();
    setUnitId(0);
    setVendorName(null);
    setFromDate(undefined);
    setToDate(undefined);
    setPage(1);

    const req = new DutyDrawBackReportReq();
    saleOrderService
      .getVendorAgingWiseReport(req)
      .then((res) => {
        if (res.status) {
          setReportData(res.data);
        } else {
          setReportData(null);
        }
      })
      .catch((err) => {
        AlertMessages.getErrorMessage(err.message);
        setReportData(null);
      });
  };

  const onFinish = () => {
    setPage(1);
    getVendorData();
  };

  const onSearch = () => {
    getVendorData();
  };

  const formatNumber = (num: string): string => {
    const parsed = parseFloat(num);
    if (isNaN(parsed)) return '-';
    return parsed.toLocaleString('en-IN', { minimumFractionDigits: 0 });
  };

  const renderRows = (entries: any[]) => {
    return entries.map((entry, index) => (
      <tr key={index} style={{ backgroundColor: '#fafafa' }}>
        <td style={{ border: '1px solid #d9d9d9', padding: '8px', textAlign: 'left' }}>{entry.unitName}</td>
        <td style={{ border: '1px solid #d9d9d9', padding: '8px', textAlign: 'left' }}>{entry.vendorName}</td>
        <td style={{ border: '1px solid #d9d9d9', padding: '8px', textAlign: 'right' }}>{formatNumber(entry.totalPendingBills)}</td>
        <td style={{ border: '1px solid #d9d9d9', padding: '8px', textAlign: 'right' }}>{formatNumber(entry.totalLess15days)}</td>
        <td style={{ border: '1px solid #d9d9d9', padding: '8px', textAlign: 'right' }}>{formatNumber(entry.total15to30days)}</td>
        <td style={{ border: '1px solid #d9d9d9', padding: '8px', textAlign: 'right' }}>{formatNumber(entry.total30to45days)}</td>
        <td style={{ border: '1px solid #d9d9d9', padding: '8px', textAlign: 'right' }}>{formatNumber(entry.total45to60days)}</td>
        <td style={{ border: '1px solid #d9d9d9', padding: '8px', textAlign: 'right' }}>{formatNumber(entry.total60to80days)}</td>
        <td style={{ border: '1px solid #d9d9d9', padding: '8px', textAlign: 'right' }}>{formatNumber(entry.total80to90days)}</td>
        <td style={{ border: '1px solid #d9d9d9', padding: '8px', textAlign: 'right' }}>{formatNumber(entry.totalafter90days)}</td>
      </tr>
    ));
  };

  const renderTotalRow = (categoryData: any, label: string) => (
    <tr style={{ backgroundColor: '#f0f0f0', fontWeight: 'bold' }}>
      <td style={{ border: '1px solid #d9d9d9', padding: '8px', textAlign: 'left' }}></td>
      <td style={{ border: '1px solid #d9d9d9', padding: '8px', textAlign: 'left' }}>{label}</td>
      <td style={{ border: '1px solid #d9d9d9', padding: '8px', textAlign: 'right' }}>{formatNumber(categoryData.totalPendingBills)}</td>
      <td style={{ border: '1px solid #d9d9d9', padding: '8px', textAlign: 'right' }}>{formatNumber(categoryData.totalLess15days)}</td>
      <td style={{ border: '1px solid #d9d9d9', padding: '8px', textAlign: 'right' }}>{formatNumber(categoryData.total15to30days)}</td>
      <td style={{ border: '1px solid #d9d9d9', padding: '8px', textAlign: 'right' }}>{formatNumber(categoryData.total30to45days)}</td>
      <td style={{ border: '1px solid #d9d9d9', padding: '8px', textAlign: 'right' }}>{formatNumber(categoryData.total45to60days)}</td>
      <td style={{ border: '1px solid #d9d9d9', padding: '8px', textAlign: 'right' }}>{formatNumber(categoryData.total60to80days)}</td>
      <td style={{ border: '1px solid #d9d9d9', padding: '8px', textAlign: 'right' }}>{formatNumber(categoryData.total80to90days)}</td>
      <td style={{ border: '1px solid #d9d9d9', padding: '8px', textAlign: 'right' }}>{formatNumber(categoryData.totalafter90days)}</td>
    </tr>
  );

  const renderGrandTotal = () => {
    if (!reportData?.grandTotal) return null;
    return (
      <tr style={{ backgroundColor: '#d9d9d9', fontWeight: 'bold' }}>
        <td style={{ border: '1px solid #d9d9d9', padding: '8px', textAlign: 'left' }}></td>
        <td style={{ border: '1px solid #d9d9d9', padding: '8px', textAlign: 'left' }}>Grand Total</td>
        <td style={{ border: '1px solid #d9d9d9', padding: '8px', textAlign: 'right' }}>{formatNumber(reportData.grandTotal.totalPendingBills)}</td>
        <td style={{ border: '1px solid #d9d9d9', padding: '8px', textAlign: 'right' }}>{formatNumber(reportData.grandTotal.totalLess15days)}</td>
        <td style={{ border: '1px solid #d9d9d9', padding: '8px', textAlign: 'right' }}>{formatNumber(reportData.grandTotal.total15to30days)}</td>
        <td style={{ border: '1px solid #d9d9d9', padding: '8px', textAlign: 'right' }}>{formatNumber(reportData.grandTotal.total30to45days)}</td>
        <td style={{ border: '1px solid #d9d9d9', padding: '8px', textAlign: 'right' }}>{formatNumber(reportData.grandTotal.total45to60days)}</td>
        <td style={{ border: '1px solid #d9d9d9', padding: '8px', textAlign: 'right' }}>{formatNumber(reportData.grandTotal.total60to80days)}</td>
        <td style={{ border: '1px solid #d9d9d9', padding: '8px', textAlign: 'right' }}>{formatNumber(reportData.grandTotal.total80to90days)}</td>
        <td style={{ border: '1px solid #d9d9d9', padding: '8px', textAlign: 'right' }}>{formatNumber(reportData.grandTotal.totalafter90days)}</td>
      </tr>
    );
  };

  const renderCategorySection = (title: string, categoryData: any) => {
    if (!categoryData || !categoryData.entries || categoryData.entries.length === 0) return null;

    return (
      <>
        <tr style={{ backgroundColor: '#ffcccc', fontWeight: 'bold' }}>
          <td style={{ border: '1px solid #d9d9d9', padding: '8px', textAlign: 'left' }}></td>
          <td style={{ border: '1px solid #d9d9d9', padding: '8px', textAlign: 'left' }}>{title}</td>
          <td style={{ border: '1px solid #d9d9d9', padding: '8px', textAlign: 'right' }}>{formatNumber(categoryData.totalPendingBills)}</td>
          <td style={{ border: '1px solid #d9d9d9', padding: '8px', textAlign: 'right' }}>{formatNumber(categoryData.totalLess15days)}</td>
          <td style={{ border: '1px solid #d9d9d9', padding: '8px', textAlign: 'right' }}>{formatNumber(categoryData.total15to30days)}</td>
          <td style={{ border: '1px solid #d9d9d9', padding: '8px', textAlign: 'right' }}>{formatNumber(categoryData.total30to45days)}</td>
          <td style={{ border: '1px solid #d9d9d9', padding: '8px', textAlign: 'right' }}>{formatNumber(categoryData.total45to60days)}</td>
          <td style={{ border: '1px solid #d9d9d9', padding: '8px', textAlign: 'right' }}>{formatNumber(categoryData.total60to80days)}</td>
          <td style={{ border: '1px solid #d9d9d9', padding: '8px', textAlign: 'right' }}>{formatNumber(categoryData.total80to90days)}</td>
          <td style={{ border: '1px solid #d9d9d9', padding: '8px', textAlign: 'right' }}>{formatNumber(categoryData.totalafter90days)}</td>
        </tr>
        {renderRows(categoryData.entries)}
      </>
    );
  };

  return (
    <div>
      <Card
        size="small"
        title={<span style={{ color: 'white' }}>Vendor Payable Aging Report</span>}
        style={{ textAlign: 'center', border: '1px solid #d9d9d9', borderRadius: '4px', background: '#fff' }}
        headStyle={{ backgroundColor: '#69c0ff', border: 0 }}
        extra={
          <>
            <ReactHTMLTableToExcel
              id="excel-button"
              table="vendor-aging-wise-report"
              filename="Vendor-aging-wise-report"
              sheet="Sheet 1"
              buttonText="Get Excel"
              className="ant-btn ant-btn"
              style={{ backgroundColor: '#1890ff', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '4px', cursor: 'pointer', marginRight: '30px' }}
            />
          </>
        }
      >
        <Form form={form} onFinish={onFinish}>
          <Row gutter={24}>
            <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 6 }} lg={{ span: 6 }} xl={{ span: 6 }}>
              <Form.Item name="unitId" label="Unit">
                <Select
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) => option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}
                  placeholder="Select Unit"
                  allowClear
                  style={{ width: '100%' }}
                  onChange={handleUnit}
                >
                  {plantData.map((dropData) => (
                    <Option key={dropData.plantId} value={dropData.plantId}>
                      {dropData.plantCode}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col>
              <Form.Item label="Invoice Date" name="invoiceDate">
                <RangePicker onChange={handleDateRange} />
              </Form.Item>
            </Col>
               <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 6 }} lg={{ span: 6 }} xl={{ span: 6 }}>
                          <Form.Item name="vendor" label="Vendor ">
                            <Select
                              showSearch
                              optionFilterProp="children"
                              filterOption={(input, option) => option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}
                              placeholder="Select Vendor"
                              allowClear
                              style={{ width: '100%' }}
                              onChange={handleVendor}
                            >
                              {vendorsData.filter(v => v.vendorName !== null).map((dropData) => (
                                <Option key={dropData.vendorId} value={dropData.vendorId}>
                                  {dropData.vendorName}
                                </Option>
                              ))}
                            </Select>
                          </Form.Item>
                        </Col>
            <Col>
              <Form.Item>
                <Button type="primary" htmlType="submit">
                  Search
                </Button>
              </Form.Item>
            </Col>
            <Col>
              <Form.Item>
                <Button onClick={onReset}>
                  Reset
                </Button>
              </Form.Item>
            </Col>
          </Row>
        </Form>

        {reportData && (
           <div style={{ overflowX: 'auto', width: '100%' }}>
          <table id="vendor-aging-wise-report" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px', backgroundColor: '#fff', fontFamily: 'Arial, sans-serif' }}>
            <thead>
              <tr>
                <th style={{ border: '1px solid #d9d9d9', padding: '8px', backgroundColor: '#f0f0f0', fontWeight: 'bold', textAlign: 'center' }} rowSpan={2}>Unit Name</th>
                <th style={{ border: '1px solid #d9d9d9', padding: '8px', backgroundColor: '#f0f0f0', fontWeight: 'bold', textAlign: 'center' }} rowSpan={2}>Vendor Name</th>
                <th style={{ border: '1px solid #d9d9d9', padding: '8px', backgroundColor: '#f0f0f0', fontWeight: 'bold', textAlign: 'center' }} rowSpan={2}>Pending Bills</th>
                <th style={{ border: '1px solid #d9d9d9', padding: '8px', backgroundColor: '#f0f0f0', fontWeight: 'bold', textAlign: 'center' }} colSpan={12}>Invoice Date Range</th>
              </tr>
              <tr>
                <th style={{ border: '1px solid #d9d9d9', padding: '8px', backgroundColor: '#f0f0f0', fontWeight: 'bold', textAlign: 'center' }}>Less than 15 days</th>
                <th style={{ border: '1px solid #d9d9d9', padding: '8px', backgroundColor: '#f0f0f0', fontWeight: 'bold', textAlign: 'center' }}>15 to 30 days</th>
                <th style={{ border: '1px solid #d9d9d9', padding: '8px', backgroundColor: '#f0f0f0', fontWeight: 'bold', textAlign: 'center' }}>31 to 45 days</th>
                <th style={{ border: '1px solid #d9d9d9', padding: '8px', backgroundColor: '#f0f0f0', fontWeight: 'bold', textAlign: 'center' }}>46 to 60 days</th>
                <th style={{ border: '1px solid #d9d9d9', padding: '8px', backgroundColor: '#f0f0f0', fontWeight: 'bold', textAlign: 'center' }}>61 to 80 days</th>
                <th style={{ border: '1px solid #d9d9d9', padding: '8px', backgroundColor: '#f0f0f0', fontWeight: 'bold', textAlign: 'center' }}>81 to 90 days</th>
                <th style={{ border: '1px solid #d9d9d9', padding: '8px', backgroundColor: '#f0f0f0', fontWeight: 'bold', textAlign: 'center' }}>Above 90 days </th>
              </tr>
            </thead>
            <tbody>
              {renderCategorySection('Packing Materials', reportData.packingMaterials)}
              {renderCategorySection('General Materials', reportData.generalMaterials)}
              {renderGrandTotal()}
            </tbody>
          </table>
          </div>
        )}
      </Card>
    </div>
  );
};

export default VendorAgingWiseReport;